import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import AgoraRTC from "agora-rtc-sdk-ng";
import { getBaseUrl } from "../utils";

const baseUrl = getBaseUrl();
const APP_ID = "35e6d26c9b11419e9d1493f857c3e81c";

const fetchToken = async (channelName) => {
    try {
        const response = await fetch(`${baseUrl}user_api/agora_token?channel=${channelName}`);
        const data = await response.json();
        if (!data.token || !data.uid) {
            throw new Error("Invalid token received from server.");
        }
        console.log("Fetched Token:", data.token, "UID:", data.uid);
        return data;
    } catch (error) {
        console.error("Error fetching Agora token:", error);
        return null;
    }
};

const AgoraCallPage = () => {
    const { channelName = "CHN-1740736508764" } = useParams();
    const navigate = useNavigate();

    const clientRef = useRef(null);
    const localVideoTrackRef = useRef(null);
    const localAudioTrackRef = useRef(null);
    const [remoteUsers, setRemoteUsers] = useState({});
    const [tokenData, setTokenData] = useState(null);
    const [isAudioMuted, setIsAudioMuted] = useState(false);
    const [isVideoStopped, setIsVideoStopped] = useState(false);

    useEffect(() => {
        let isMounted = true;

        const initAgora = async () => {
            if (!channelName) return;

            if (clientRef.current) {
                console.warn("Agora client already initialized.");
                return;
            }

            console.log("Fetching token...");
            const fetchedTokenData = await fetchToken(channelName);
            if (!fetchedTokenData || !isMounted) return;

            setTokenData(fetchedTokenData);
            const { token, uid } = fetchedTokenData;

            console.log("Joining channel:", channelName);
            const agoraClient = AgoraRTC.createClient({ mode: "rtc", codec: "vp8" });
            clientRef.current = agoraClient;

            try {
                await agoraClient.join(APP_ID, channelName, token, uid);
                if (!isMounted) return;

                const localVideoTrack = await AgoraRTC.createCameraVideoTrack();
                const localAudioTrack = await AgoraRTC.createMicrophoneAudioTrack();
                localVideoTrackRef.current = localVideoTrack;
                localAudioTrackRef.current = localAudioTrack;

                await agoraClient.publish([localVideoTrack, localAudioTrack]);
                localVideoTrack.play("local-video");

                // Handle remote users joining
                agoraClient.on("user-published", async (user, mediaType) => {
                    console.log(`User published: ${user.uid} (${mediaType})`);
                    await agoraClient.subscribe(user, mediaType);
                    setRemoteUsers((prevUsers) => ({
                        ...prevUsers,
                        [user.uid]: user,
                    }));

                    if (mediaType === "video") {
                        user.videoTrack.play(`remote-video-${user.uid}`);
                    }

                    if (mediaType === "audio") {
                        user.audioTrack.play();
                    }
                });

                // Auto-subscribe to existing remote users
                agoraClient.remoteUsers.forEach(async (user) => {
                    console.log(`Subscribing to existing user ${user.uid}`);
                    await agoraClient.subscribe(user, "video");
                    await agoraClient.subscribe(user, "audio");

                    if (user.videoTrack) {
                        user.videoTrack.play(`remote-video-${user.uid}`);
                    }
                    if (user.audioTrack) {
                        user.audioTrack.play();
                    }
                });

                // Handle remote user leaving
                agoraClient.on("user-unpublished", (user) => {
                    console.log(`User left: ${user.uid}`);
                    setRemoteUsers((prevUsers) => {
                        const updatedUsers = { ...prevUsers };
                        delete updatedUsers[user.uid];
                        return updatedUsers;
                    });
                });

            } catch (error) {
                console.error("Agora Error:", error);
                navigate(-1);
            }
        };

        initAgora();

        return () => {
            isMounted = false;
            if (clientRef.current) {
                console.log("Leaving channel...");
                clientRef.current.leave();
                localVideoTrackRef.current?.stop();
                localVideoTrackRef.current?.close();
                localAudioTrackRef.current?.stop();
                localAudioTrackRef.current?.close();
                clientRef.current = null;
            }
        };
    }, [channelName]);

    const handleMuteAudio = () => {
        if (localAudioTrackRef.current) {
            isAudioMuted ? localAudioTrackRef.current.setMuted(false) : localAudioTrackRef.current.setMuted(true);
            setIsAudioMuted(!isAudioMuted);
        }
    };

    const handleToggleVideo = () => {
        if (localVideoTrackRef.current) {
            if (isVideoStopped) {
                localVideoTrackRef.current.setMuted(false);
                localVideoTrackRef.current.play("local-video");
            } else {
                localVideoTrackRef.current.setMuted(true);
                localVideoTrackRef.current.stop();
            }
            setIsVideoStopped(!isVideoStopped);
        }
    };

    const handleLeaveCall = async () => {
        if (clientRef.current) {
            console.log("Leaving call...");
            await clientRef.current.leave();
            localVideoTrackRef.current?.stop();
            localVideoTrackRef.current?.close();
            localAudioTrackRef.current?.stop();
            localAudioTrackRef.current?.close();
            clientRef.current = null;
        }
        navigate(-1);
    };

    return (
        <div className="w-full h-screen flex flex-col items-center justify-center bg-gray-900 text-white relative">
            <h2 className="text-2xl font-bold mb-4">Agora Video Call</h2>

            {/* Full Screen Remote Video */}
            <div className="w-full h-full flex items-center justify-center relative">
                {Object.keys(remoteUsers).length > 0 ? (
                    Object.keys(remoteUsers).map((uid) => (
                        <div key={uid} id={`remote-video-${uid}`} className="w-full h-full bg-black flex items-center justify-center">
                            <span className="text-gray-300"></span>
                        </div>
                    ))
                ) : (
                    <div className="w-full h-full flex items-center justify-center bg-black">
                        <span className="text-gray-400">Waiting for remote user...</span>
                    </div>
                )}

                {/* Small Local Video Preview (Bottom Right) */}
                <div className="absolute bottom-4 right-4 w-36 h-24 bg-gray-700 border-2 border-white rounded-lg flex items-center justify-center">
                    <div id="local-video" className="w-full h-full flex items-center justify-center">
                        <span className="text-gray-300"></span>
                    </div>
                </div>
            </div>

            {/* Controls */}
            <div className="absolute bottom-4 left-4 flex gap-4">
                <button className="px-4 py-2 bg-blue-500 text-white rounded-lg" onClick={handleMuteAudio}>
                    {isAudioMuted ? "Unmute" : "Mute"}
                </button>

                <button className="px-4 py-2 bg-yellow-500 text-white rounded-lg" onClick={handleToggleVideo}>
                    {isVideoStopped ? "Start Video" : "Stop Video"}
                </button>
            </div>

            {/* Leave Call Button */}
            <button className="absolute top-4 right-4 px-6 py-2 bg-red-500 text-white rounded-lg" onClick={handleLeaveCall}>
                Leave Call
            </button>
        </div>
    );
};

export default AgoraCallPage;
